// src/components/ProjectManagement/CreateNewProductModal.jsx
import React, { useState } from 'react';
import { createCustomProduct } from '../../utils/productTemplates';

const CreateNewProductModal = ({ onClose, onProductCreated }) => {
  const [step, setStep] = useState(1);
  const [productData, setProductData] = useState({
    name: '',
    category: 'custom',
    description: ''
  });
  const [tasks, setTasks] = useState([
    { name: '', days: 4, required: true, description: '' }
  ]);
  const [loading, setLoading] = useState(false);

  const addTask = () => {
    setTasks([...tasks, { name: '', days: 4, required: false, description: '' }]);
  };

  const removeTask = (index) => {
    if (tasks.length === 1) {
      alert('You need at least one task');
      return;
    }
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const updateTask = (index, field, value) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };

  const handleSubmit = async () => {
    if (step === 1) {
      if (!productData.name.trim()) {
        alert('Please enter a product name');
        return;
      }
      setStep(2);
      return;
    }
    
    if (step === 2) {
      const validTasks = tasks.filter(t => t.name.trim());
      if (validTasks.length === 0) {
        alert('Please add at least one task');
        return;
      }
      
      setLoading(true);
      try {
        const newProduct = await createCustomProduct(
          productData.name, 
          productData.category, 
          tasks.filter(t => t.name.trim())
        );
        alert(`✅ Product "${productData.name}" created successfully!`);
        onProductCreated(newProduct);
        onClose();
      } catch (error) {
        console.error('Error creating product:', error);
        alert('Failed to create product');
      } finally {
        setLoading(false);
      }
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  if (step === 1) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '500px' }} onClick={stopPropagation}>
          <h2>✨ Create New Product</h2>
          <div className="step-indicator">
            <div className="step active">1. Product Details</div>
            <div className="step">2. Define Tasks</div>
          </div>

          <label>Product Name *</label>
          <input 
            type="text" 
            value={productData.name} 
            onChange={(e) => setProductData({...productData, name: e.target.value})}
            placeholder="e.g., Ottoman Bed, TV Cabinet, Bookshelf"
            autoFocus
          />

          <label>Category</label>
          <select value={productData.category} onChange={(e) => setProductData({...productData, category: e.target.value})}>
            <option value="living-room">🛋️ Living Room</option>
            <option value="bedroom">🛏️ Bedroom</option>
            <option value="kitchen">🍳 Kitchen</option>
            <option value="dining">🍽️ Dining Room</option>
            <option value="office">💼 Office</option>
            <option value="outdoor">🌳 Outdoor/Garden</option>
            <option value="children">🧸 Children's Room</option>
            <option value="custom">✨ Custom</option>
          </select>

          <label>Description (Optional)</label>
          <textarea 
            rows="3" 
            value={productData.description} 
            onChange={(e) => setProductData({...productData, description: e.target.value})}
            placeholder="Describe this product type..."
          />

          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
            <button className="assign-btn" onClick={handleSubmit}>Next: Define Tasks →</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }} onClick={stopPropagation}>
        <h2>📋 Define Tasks for {productData.name}</h2>
        <div className="step-indicator">
          <div className="step completed">✓ 1. Product Details</div>
          <div className="step active">2. Define Tasks</div>
        </div>

        <p className="info-text">💡 These tasks will be automatically added when this product is selected in future projects.</p>

        <div className="tasks-definition-list">
          {tasks.map((task, index) => (
            <div key={index} className="task-definition-item">
              <div className="task-def-header">
                <strong>Task {index + 1}</strong>
                <button className="remove-def-task-btn" onClick={() => removeTask(index)}>✕</button>
              </div>
              <input 
                type="text" 
                placeholder="Task name *" 
                value={task.name}
                onChange={(e) => updateTask(index, 'name', e.target.value)}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label>Duration (days)</label>
                  <input 
                    type="number" 
                    value={task.days}
                    onChange={(e) => updateTask(index, 'days', parseInt(e.target.value))}
                    min="1"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input 
                      type="checkbox" 
                      checked={task.required}
                      onChange={(e) => updateTask(index, 'required', e.target.checked)}
                    />
                    Required task
                  </label>
                </div>
              </div>
              <textarea 
                placeholder="Task description (optional)"
                rows="2"
                value={task.description}
                onChange={(e) => updateTask(index, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>

        <button className="add-task-def-btn" onClick={addTask}>+ Add Another Task</button>

        <div className="modal-actions">
          <button className="cancel-btn" onClick={() => setStep(1)}>← Back</button>
          <button className="assign-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : '✓ Create Product Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewProductModal;